// Dice3D.tsx
import React, { useRef, useState, useMemo } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import * as THREE from 'three';

const diceNumbers = [3, 4, 1, 6, 2, 5];

const finalRotations: { [key: number]: [number, number, number] } = {
  1: [Math.PI / 2, 0, 0],
  2: [0, 0, 0],
  3: [0, -Math.PI / 2, 0],
  4: [0, Math.PI / 2, 0],
  5: [0, Math.PI, 0],
  6: [-Math.PI / 2, 0, 0],
};

const createDiceTexture = (num: number): THREE.CanvasTexture => {
  const size = 256;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const context = canvas.getContext('2d');
  if (context) {
    // Create a background gradient
    const gradient = context.createLinearGradient(0, 0, size, size);
    gradient.addColorStop(0, '#ffffff');
    gradient.addColorStop(1, '#e0e0e0');
    context.fillStyle = gradient;
    context.fillRect(0, 0, size, size);
    // Draw a thick black border
    context.strokeStyle = '#000000';
    context.lineWidth = 6;
    context.strokeRect(0, 0, size, size);
    // Draw the number in the center
    context.fillStyle = '#000000';
    context.font = 'bold 150px Arial';
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    context.fillText(num.toString(), size / 2, size / 2);
  }
  return new THREE.CanvasTexture(canvas);
};

export const Dice: React.FC<{ onRollComplete: (face: number) => void }> = ({ onRollComplete }) => {
  const meshRef = useRef<THREE.Mesh>(null!);
  const rollDuration = 1.0; // seconds
  const [rolling, setRolling] = useState(false);
  const [startTime, setStartTime] = useState<number | null>(null);

  // Generate a random slide direction on roll start (XY plane)
  const [slideDirection] = useState(() => {
    const angle = Math.random() * 2 * Math.PI;
    return new THREE.Vector3(Math.cos(angle), Math.sin(angle), 0);
  });

  // Create dice materials using memoization for performance
  const diceMaterials = useMemo(() => {
    return diceNumbers.map(num =>
      new THREE.MeshStandardMaterial({
        map: createDiceTexture(num),
        metalness: 0.6,
        roughness: 0.2,
      })
    );
  }, []);

  useFrame((state, delta) => {
    if (!rolling) return;
    if (startTime === null) {
      setStartTime(state.clock.getElapsedTime());
      return;
    }
    const elapsed = state.clock.getElapsedTime() - startTime;
    if (elapsed < rollDuration) {
      if (meshRef.current) {
        // Rotate randomly
        meshRef.current.rotation.x += delta * Math.random() * 10;
        meshRef.current.rotation.y += delta * Math.random() * 10;
        meshRef.current.rotation.z += delta * Math.random() * 10;
        // Slide (translate) the dice in the random direction
        meshRef.current.position.add(slideDirection.clone().multiplyScalar(delta * 0.5));
      }
    } else {
      setRolling(false);
      const finalFace = Math.floor(Math.random() * 6) + 1;
      if (meshRef.current) {
        const [rx, ry, rz] = finalRotations[finalFace];
        meshRef.current.rotation.set(rx, ry, rz);
      }
      onRollComplete(finalFace);
    }
  });

  const handleClick = () => {
    if (!rolling) {
      // Reset position and start roll
      if (meshRef.current) {
        meshRef.current.position.set(0, 0, 0);
      }
      setRolling(true);
      setStartTime(null);
    }
  };

  return (
    <mesh ref={meshRef} material={diceMaterials} onClick={handleClick} style={{ cursor: 'pointer' }}>
      <boxGeometry args={[1, 1, 1]} />
    </mesh>
  );
};

export const Dice3D: React.FC<{ onRollComplete: (face: number) => void }> = ({ onRollComplete }) => {
  return (
    <Canvas style={{ height: 300, width: 300 }}>
      <ambientLight intensity={0.5} />
      <directionalLight position={[2, 2, 2]} />
      <Dice onRollComplete={onRollComplete} />
    </Canvas>
  );
};
